import React, { useEffect, useState } from 'react';
import DreIndicadores from './DreIndicadores';
import DrePlanoAcao from './DrePlanoAcao';
import { IconBuilding, IconAlert, IconLock } from './Icons';

// Drilldown de unidades (somente leitura): lista as unidades de uma região no
// período e abre o detalhe de uma unidade. As URLs são injetadas para reuso:
//   - gerente regional -> getDreUnidades / getDreUnidadeDetalhe (escopo do SSO);
//   - visão consolidada -> getDreUnidadesRegiao / getDreVisaoUnidade (leitor global).
// A autorização é sempre feita no servidor pela rota correspondente.
const defaultUnidadesUrl = (ano, mes) => `/api/getDreUnidades?ano=${ano}&mes=${mes}`;
const defaultDetalheUrl = (filialChave, ano, mes) =>
  `/api/getDreUnidadeDetalhe?ano=${ano}&mes=${mes}&filialChave=${encodeURIComponent(filialChave)}`;

function DreUnidades({
  ano,
  mes,
  unidadesUrl = defaultUnidadesUrl,
  detalheUrl = defaultDetalheUrl
}) {
  const [unidades, setUnidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null);   // detalhe carregado
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [errorDetalhe, setErrorDetalhe] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSelected(null);
    setErrorDetalhe(null);
    fetch(unidadesUrl(ano, mes))
      .then(res => res.json())
      .then(data => {
        if (data.error) { setError(data.error); setUnidades([]); }
        else { setError(null); setUnidades(data.unidades || []); }
      })
      .catch(() => setError('Erro ao carregar unidades da região'))
      .finally(() => setLoading(false));
  }, [ano, mes, unidadesUrl]);

  const abrirDetalhe = (filialChave) => {
    setLoadingDetalhe(true);
    setErrorDetalhe(null);
    setSelected(null);
    fetch(detalheUrl(filialChave, ano, mes))
      .then(res => res.json())
      .then(data => {
        if (data.error) setErrorDetalhe(data.error);
        else setSelected(data);
      })
      .catch(() => setErrorDetalhe('Erro ao carregar detalhe da unidade'))
      .finally(() => setLoadingDetalhe(false));
  };

  if (loading) {
    return <p className="loading-text">Carregando unidades da região...</p>;
  }

  if (error) {
    return (
      <div className="access-denied-card">
        <span className="access-icon"><IconAlert /></span>
        <h3>Não foi possível carregar as unidades</h3>
        <p>{error}</p>
      </div>
    );
  }

  // Modo detalhe (somente leitura).
  if (selected) {
    return (
      <div className="dre-unidade-detalhe">
        <div className="pdf-viewer-topbar">
          <button className="back-button" onClick={() => setSelected(null)}>← Voltar às unidades</button>
          <div className="pdf-viewer-info">
            <span className="pdf-title"><IconBuilding size={18} /> Unidade {selected.filialChave}</span>
            <span className="pdf-subtitle">Somente leitura</span>
          </div>
        </div>
        <DreIndicadores
          indicadores={selected.resultados}
          justificativas={selected.justificativas || {}}
          editable={false}
        />
        <DrePlanoAcao planoAcao={selected.planoAcao} editable={false} />
      </div>
    );
  }

  return (
    <div className="dre-unidades">
      <h3 className="dre-section-title"><IconBuilding size={20} /> Unidades da Região</h3>
      {loadingDetalhe && <p className="loading-text">Carregando detalhe...</p>}
      {errorDetalhe && (
        <div className="access-denied-card">
          <span className="access-icon"><IconLock /></span>
          <h3>Acesso negado</h3>
          <p>{errorDetalhe}</p>
        </div>
      )}

      {unidades.length === 0 ? (
        <p className="empty-message">Nenhuma unidade encontrada na sua região para este período.</p>
      ) : (
        <div className="files-list">
          {unidades.map(u => (
            <div key={u.filialChave} className="file-card" onClick={() => abrirDetalhe(u.filialChave)}>
              <div className="file-icon"><IconBuilding /></div>
              <div className="file-info">
                <span className="file-name">{u.filialNome || u.filialChave}</span>
                <span className="file-meta">
                  {u.qtdJustificativas}/{u.totalIndicadores} justificativas
                  {' • '}
                  {u.temPlanoAcao ? 'com plano de ação' : 'sem plano de ação'}
                </span>
              </div>
              <button className="view-button">Ver detalhe</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DreUnidades;
